import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchTickets } from '../store/ticketsSlice';
import { shouldClientUserSeeNotification } from '../utils/notificationSecurity';
import NotificationBanner from './NotificationBanner';

const NotificationManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useAuth();
  const [activeNotifications, setActiveNotifications] = useState([]);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const { notifications } = useSelector((state) => state.notifications);
  const uiState = useSelector((state) => state.ui);

  const buildTicketParams = useCallback((listKey) => {
    // Get UI state for the specific list
    const uiList = (uiState.perList && uiState.perList[listKey]) || {
      search: uiState.search,
      statusFilter: uiState.statusFilter,
      priorityFilter: uiState.priorityFilter,
      assignedToFilter: uiState.assignedToFilter,
      createdFrom: uiState.createdFrom,
      createdRange: uiState.createdRange,
      createdTo: uiState.createdTo,
      productFilter: uiState.productFilter,
      currentPage: uiState.currentPage,
      itemsPerPage: uiState.itemsPerPage,
    };

    const params = {
      skip: ((uiList.currentPage || 1) - 1) * (uiList.itemsPerPage || 10),
      limit: uiList.itemsPerPage || 10,
    };

    const isMyTickets = listKey.startsWith('mine:');
    if (isMyTickets) {
      params.assigned_to_id = user?.id;
    } else if (uiList.assignedToFilter && uiList.assignedToFilter !== 'all') {
      if (uiList.assignedToFilter === 'unassigned') {
        params.assigned_unassigned = true;
      } else {
        params.assigned_to_id = uiList.assignedToFilter;
      }
    }
    
    if (uiList.search) params.search = uiList.search;
    if (uiList.statusFilter && uiList.statusFilter !== 'all') params.status = uiList.statusFilter;
    if (uiList.priorityFilter && uiList.priorityFilter !== 'all') params.priority_id = uiList.priorityFilter;
    if (uiList.productFilter && uiList.productFilter !== 'all') params.product_id = uiList.productFilter;
    
    // Handle created date filters
    if (uiList.createdFrom) params.created_from = uiList.createdFrom;
    if (uiList.createdTo) params.created_to = uiList.createdTo;

    return params;
  }, [uiState, user]);

  const refreshTicketListIfNeeded = useCallback((notification) => {
    // Check if notification is ticket-related
    const isTicketNotification = 
      notification.entity_type === 'ticket' || 
      notification.ticket_id || 
      notification.category === 'ticket';

    if (!isTicketNotification) {
      return;
    }

    // Check if user is on any tickets page
    const isOnTicketsList = location.pathname === '/tickets';
    const isOnMyTickets = location.pathname === '/my-tickets';
    const isOnClientTickets = location.pathname === '/client-tickets';

    if (isOnTicketsList || isOnMyTickets || isOnClientTickets) {
      // Determine the correct listKey based on the current page
      let listKey = 'all';
      if (isOnMyTickets && user?.id) {
        listKey = `mine:${user.id}`;
      }

      // Build params from current UI state to preserve filters
      const params = buildTicketParams(listKey);
      
      // Trigger a refresh with current filter parameters
      dispatch(fetchTickets({ params, listKey }));
    }
  }, [location.pathname, user, buildTicketParams, dispatch]);

  useEffect(() => {
    // Listen for new notifications
    if (notifications.length > 0) {
      const latestNotification = notifications[0];
      
      // Check if this is a NEW notification (different ID from last seen)
      const isNewNotification = latestNotification.id !== lastNotificationId;
      
      if (isNewNotification && !latestNotification.is_read) {
        // Auto-refresh ticket list FIRST (before security filter)
        // This ensures list refreshes even if notification is filtered for display
        refreshTicketListIfNeeded(latestNotification);
        
        // CRITICAL SECURITY: Filter notifications for client users
        if (!shouldClientUserSeeNotification(latestNotification, user)) {
          setLastNotificationId(latestNotification.id); // Mark as seen but don't show
          return;
        }
        
        // Update last seen ID
        setLastNotificationId(latestNotification.id);
        
        // Add to active notifications (max 3 at a time, stacked)
        setActiveNotifications(prev => {
          // Double-check for duplicates before adding
          const isDuplicate = prev.some(n => n.id === latestNotification.id);
          if (isDuplicate) {
            return prev;
          }
          const updated = [latestNotification, ...prev];
          return updated.slice(0, 3); // Keep only 3 most recent
        });
      }
    }
  }, [notifications, lastNotificationId, refreshTicketListIfNeeded, user]);

  const handleClose = (notificationId) => {
    setActiveNotifications(prev => 
      prev.filter(n => n.id !== notificationId)
    );
  };

  return (
    <div className="fixed top-0 right-0 z-[10000] p-4 space-y-3 pointer-events-none">
      <div className="space-y-3 pointer-events-auto">
        {activeNotifications.map((notification, index) => (
          <div
            key={notification.id}
            style={{
              marginTop: `${index * 10}px`,
              opacity: 1 - (index * 0.2),
              zIndex: 10000 - index
            }}
          >
            <NotificationBanner
              notification={notification}
              onClose={() => handleClose(notification.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationManager;
